import React from 'react';
import { useTranslation } from 'react-i18next';
import warningIcon from '../assets/images/warning_icon.svg';
import { COLORS } from '../shared/colors';
import './ScrumWarningModal.scss';

import { Button, Box, CircularProgress } from '@mui/material';

const ScrumWarningModal = ({
  color = COLORS.red.scrumWarning,
  width = '500px',
  title,
  descp,
  descpOptions,
  isOpen,
  onClose,
  icon = warningIcon,
  primaryAction,
  secondaryAction
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const displayTitle = title ? t(title) : '';
  const displayDescp = descp ? t(descp, descpOptions) : t('scrumWarningModal.defaultDescription');
  const isPrimaryLoading = Boolean(primaryAction?.loading);
  const isSecondaryLoading = Boolean(secondaryAction?.loading);
  const iconElement = React.isValidElement(icon)
    ? React.cloneElement(icon, {
        className: `scrum-warning-modal-icon ${icon.props.className || ''}`.trim(),
        style: { color, ...(icon.props.style || {}) }
      })
    : <img src={icon} alt="Warning" className="scrum-warning-modal-icon" />;

  return (
    <div className="scrum-warning-modal-overlay">
      <div className="scrum-warning-modal-box" style={{ width: width }}>
        {/* Background color tint layer */}
        <div className="scrum-warning-modal-tint" style={{ backgroundColor: color }} />
        
        {/* Close Button */}
        {!(secondaryAction && (secondaryAction.isCancel || String(secondaryAction.text).toLowerCase().includes('cancel'))) && (
          <button className="scrum-warning-modal-close" onClick={onClose} aria-label="Close modal">
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1L13 13M1 13L13 1" />
            </svg>
          </button>
        )}

        {/* Content */}
        <div className="scrum-warning-modal-content">
          <div className="scrum-warning-modal-header">
            {iconElement}
            {displayTitle && <h2 style={{ color: color }}>{displayTitle}</h2>}
          </div>

          <hr className="scrum-warning-modal-separator" style={{ backgroundColor: color }} />

          <p className="scrum-warning-modal-descp">
            {displayDescp}
          </p>

          {/* Actions */}
          {(primaryAction || secondaryAction) && (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
              {secondaryAction && (
                <Button 
                  onClick={secondaryAction.onClick} 
                  variant="outlined"
                  disabled={secondaryAction.disabled || isPrimaryLoading || isSecondaryLoading}
                  sx={{ 
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: '8px',
                    minWidth: '120px',
                    px: 3,
                    py: 1,
                    color: COLORS.slate[600],
                    bgcolor: COLORS.white,
                    borderColor: COLORS.slate[300],
                    '&:hover': {
                      bgcolor: COLORS.slate[100],
                      borderColor: COLORS.slate[400]
                    }
                  }}
                >
                  {secondaryAction.text}
                </Button>
              )}
              {primaryAction && (
                <Button 
                  onClick={primaryAction.onClick} 
                  variant="contained" 
                  disabled={primaryAction.disabled || isPrimaryLoading || isSecondaryLoading}
                  startIcon={isPrimaryLoading ? <CircularProgress size={18} color="inherit" /> : null}
                  sx={{ 
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: '8px',
                    minWidth: '120px',
                    px: 3,
                    py: 1,
                    color: COLORS.white,
                    bgcolor: color,
                    boxShadow: 'none',
                    '&:hover': { 
                      bgcolor: color,
                      boxShadow: COLORS.ui.shadowHeavy,
                      filter: 'brightness(0.9)'
                    } 
                  }}
                >
                  {isPrimaryLoading ? (primaryAction.loadingText || primaryAction.text) : primaryAction.text}
                </Button>
              )}
            </Box>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScrumWarningModal;
